import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { MenuItemCard } from '@molecules/index';
import { ProductCustomizationModal } from '@organisms/index';
import { Badge, Icon, Button } from '@atoms/index';
import { catalogService } from '@services/catalogService';
import { useCartStore } from '@store/cartStore';
import { formatRating } from '@/utils/format';
import { formatCurrency } from '@/utils/currency';
import { cn } from '@utils/cn';
import type { Restaurant, MenuCategory, MenuItem } from '@/types';

const TURKEY_TZ = 'Europe/Istanbul';

// Header (h-20 = 80px) + horizontal sticky tab bar (~56px) + breathing room.
// Desktop has the vertical sidebar instead of the sticky bar, so the offset
// shrinks back to roughly header height + spacing.
const SCROLL_MARGIN_CLASS = 'scroll-mt-[160px] xl:scroll-mt-28';

// Used by both the IntersectionObserver and programmatic-scroll guards so
// scrollspy doesn't fight the smooth scroll triggered by a tab click.
const PROGRAMMATIC_SCROLL_LOCK_MS = 700;

function formatNextOpen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();

  const todayInTurkey = now.toLocaleDateString('en-CA', { timeZone: TURKEY_TZ });
  const tomorrowDate = new Date(now.getTime() + 86400000);
  const tomorrowInTurkey = tomorrowDate.toLocaleDateString('en-CA', { timeZone: TURKEY_TZ });
  const openDayInTurkey = d.toLocaleDateString('en-CA', { timeZone: TURKEY_TZ });

  const timeStr = d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: TURKEY_TZ });

  if (openDayInTurkey === todayInTurkey) return `Bugün ${timeStr}`;
  if (openDayInTurkey === tomorrowInTurkey) return `Yarın ${timeStr}`;

  const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const dayOfWeek = new Date(d).toLocaleDateString('en-US', { weekday: 'long', timeZone: TURKEY_TZ });
  const dayNameIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(dayOfWeek);
  return `${dayNames[dayNameIndex >= 0 ? dayNameIndex : 0]} ${timeStr}`;
}

function formatNextClose(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: TURKEY_TZ });
}

const RestaurantPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setRestaurant, addItem, setServiceZone, items: cartItems } = useCartStore();

  const [restaurant, setRestaurantData] = useState<Restaurant | null>(null);
  const [menu, setMenu] = useState<MenuCategory[]>([]);
  const [promotions, setPromotions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const horizontalTabBarRef = useRef<HTMLDivElement>(null);
  const tabRefsMap = useRef<Map<string, HTMLButtonElement>>(new Map());
  const sectionRefsMap = useRef<Map<string, HTMLElement>>(new Map());
  // True while a smooth scroll triggered by tab click is in flight — pauses
  // scrollspy so the observer doesn't bounce the active tab during animation.
  const programmaticScrollLockRef = useRef(false);
  const programmaticScrollTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!id) return;
    setRestaurantData(null);
    setMenu([]);
    setPromotions([]);
    setError(null);
    setSelectedProduct(null);
    setActiveCategoryId(null);
    loadRestaurantData(id);
  }, [id]);

  const loadRestaurantData = async (restaurantId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const [restaurantData, menuResponse, promotionsData] = await Promise.all([
        catalogService.getRestaurant(restaurantId),
        catalogService.getRestaurantMenu(restaurantId),
        catalogService.getPromotions(restaurantId).catch(() => []),
      ]);

      const rawCategories = Array.isArray(menuResponse)
        ? menuResponse
        : ((menuResponse as any)?.categories || []);

      const normalizedCategories = rawCategories.map((cat: any) => ({
        ...cat,
        menuItems: (cat.products || cat.menuItems || []).map((product: any) => ({
          ...product,
          optionGroups: (product.optionGroups || []).map((og: any) => ({
            ...og,
            isRequired: og.isRequired !== undefined ? og.isRequired : og.required || false,
            required: og.required !== undefined ? og.required : og.isRequired || false,
            minSelections: og.minSelect !== undefined ? og.minSelect : og.minSelections || 0,
            maxSelections: og.maxSelect !== undefined ? og.maxSelect : og.maxSelections || null,
            options: (og.options || []).map((opt: any) => ({
              ...opt,
              available: opt.isActive !== undefined ? opt.isActive : (opt.available !== undefined ? opt.available : true),
              isActive: opt.isActive !== undefined ? opt.isActive : (opt.available !== undefined ? opt.available : true),
            })),
          })),
          modifierGroups: (product.modifierGroups || []).map((mg: any) => ({
            ...mg,
            minSelection: mg.minSelection !== undefined ? mg.minSelection : 0,
            maxSelection: mg.maxSelection !== undefined ? mg.maxSelection : null,
            modifierOptions: (mg.modifierOptions || []).map((mo: any) => ({
              ...mo,
              isActive: mo.isActive !== undefined ? mo.isActive : true,
            })),
          })),
        })),
      }));

      setRestaurantData(restaurantData);
      setMenu(normalizedCategories);
      setPromotions(promotionsData || []);
      setRestaurant(restaurantData);

      if (restaurantData.serviceZoneId && cartItems.length === 0) {
        await setServiceZone(restaurantData.serviceZoneId).catch(err => {
          console.error('Failed to set service zone in cart:', err);
        });
      }

      if (normalizedCategories.length > 0) {
        const hash = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
        const hashTarget = hash.startsWith('kategori-') ? hash.replace('kategori-', '') : null;
        const initial = hashTarget && normalizedCategories.some((c: MenuCategory) => c.id === hashTarget)
          ? hashTarget
          : normalizedCategories[0].id;
        setActiveCategoryId(initial);
      }
    } catch (err) {
      setError('Restoran bilgileri yüklenirken bir hata oluştu.');
      console.error('Load restaurant error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Scroll the active horizontal tab into view whenever it changes —
  // mirrors the UX in Uber Eats / Wolt / Trendyol Yemek where the tab
  // strip auto-follows the user's vertical position.
  useEffect(() => {
    if (!activeCategoryId) return;
    const tab = tabRefsMap.current.get(activeCategoryId);
    const container = horizontalTabBarRef.current;
    if (!tab || !container) return;
    const tabRect = tab.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const offset = (tabRect.left + tabRect.right) / 2 - (containerRect.left + containerRect.right) / 2;
    container.scrollBy({ left: offset, behavior: 'smooth' });
  }, [activeCategoryId]);

  // Scrollspy: as the user scrolls vertically through the menu, highlight
  // the section that currently occupies the top of the viewport (below
  // the header + sticky tab bar). Uses IntersectionObserver with a
  // rootMargin that effectively defines a narrow horizontal "activation
  // line" near the top — the standard pattern used by all major food
  // delivery apps.
  useEffect(() => {
    if (menu.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (programmaticScrollLockRef.current) return;

        const intersecting = entries.filter((e) => e.isIntersecting);
        if (intersecting.length === 0) return;

        intersecting.sort(
          (a, b) => a.target.getBoundingClientRect().top - b.target.getBoundingClientRect().top,
        );
        const topEntry = intersecting[0];
        const id = (topEntry.target as HTMLElement).dataset.categoryId;
        if (id) {
          setActiveCategoryId(id);
        }
      },
      {
        // Activation band: from just below the sticky tab bar down to ~45% viewport.
        // Anything entering this band becomes the active category.
        rootMargin: '-160px 0px -55% 0px',
        threshold: 0,
      },
    );

    sectionRefsMap.current.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [menu]);

  // Filter menu items based on search and category
  const filteredMenu = useMemo(() => {
    if (!searchQuery.trim()) return menu;

    const query = searchQuery.toLowerCase().trim();
    return menu
      .map((category) => ({
        ...category,
        menuItems: (category.menuItems || (category as any).products || []).filter(
          (item: MenuItem) =>
            item.name.toLowerCase().includes(query) ||
            (item.description && item.description.toLowerCase().includes(query)),
        ),
      }))
      .filter((category) => (category.menuItems || (category as any).products || []).length > 0);
  }, [menu, searchQuery]);

  const handleProductClick = (menuItem: MenuItem) => {
    setSelectedProduct(menuItem);
  };

  const handleCloseModal = () => {
    setSelectedProduct(null);
  };

  const handleAddToCart = async (item: {
    menuItemId: string;
    quantity: number;
    selectedOptions: any[];
    specialInstructions?: string;
    removedIngredientIds?: string[];
    menuItem?: MenuItem;
    itemTotal?: number;
  }) => {
    try {
      await addItem({
        menuItemId: item.menuItemId,
        quantity: item.quantity,
        selectedOptions: item.selectedOptions,
        specialInstructions: item.specialInstructions,
        removedIngredientIds: item.removedIngredientIds,
        menuItem: item.menuItem,
        itemTotal: item.itemTotal,
      });
      setSelectedProduct(null);
    } catch (err) {
      console.error('Failed to add item to cart:', err);
    }
  };

  const scrollToCategory = useCallback((categoryId: string) => {
    setActiveCategoryId(categoryId);

    programmaticScrollLockRef.current = true;
    if (programmaticScrollTimerRef.current) {
      window.clearTimeout(programmaticScrollTimerRef.current);
    }
    programmaticScrollTimerRef.current = window.setTimeout(() => {
      programmaticScrollLockRef.current = false;
    }, PROGRAMMATIC_SCROLL_LOCK_MS);

    const element = sectionRefsMap.current.get(categoryId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Shareable / back-button friendly URL state, no history pollution.
    navigate({ ...location, hash: `#kategori-${categoryId}` }, { replace: true });
  }, [navigate, location]);

  useEffect(() => {
    return () => {
      if (programmaticScrollTimerRef.current) {
        window.clearTimeout(programmaticScrollTimerRef.current);
      }
    };
  }, []);

  const setSectionRef = useCallback((categoryId: string) => (el: HTMLElement | null) => {
    if (el) {
      sectionRefsMap.current.set(categoryId, el);
    } else {
      sectionRefsMap.current.delete(categoryId);
    }
  }, []);

  const setTabRef = useCallback((categoryId: string) => (el: HTMLButtonElement | null) => {
    if (el) {
      tabRefsMap.current.set(categoryId, el);
    } else {
      tabRefsMap.current.delete(categoryId);
    }
  }, []);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="animate-pulse">
          <div className="h-64 bg-gray-200 rounded-lg mb-6" />
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8" />
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !restaurant) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-red-600 text-lg mb-4">{error || 'Restoran bulunamadı'}</p>
          <Button onClick={() => navigate('/')}>Ana Sayfaya Dön</Button>
        </div>
      </MainLayout>
    );
  }

  const restaurantOpen = restaurant.isOpen !== undefined ? restaurant.isOpen : restaurant.isAcceptingOrders;

  const itemCountFor = (category: MenuCategory) =>
    (category.menuItems || (category as any).products || []).length;

  return (
    <MainLayout>
      <div className="mb-8">
        <article className="rounded-2xl overflow-hidden bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-gray-100">
          <header className="relative h-[200px] sm:h-[240px] md:h-[280px] w-full bg-gray-100">
            <img
              key={`banner-${id}`}
              src={restaurant.bannerUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </header>
          <div className="border-t border-gray-100 bg-white px-5 sm:px-6 py-5 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              {restaurant.logoUrl && (
                <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden bg-gray-50 ring-1 ring-gray-100">
                  <img
                    key={`logo-${id}`}
                    src={restaurant.logoUrl}
                    alt=""
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
                    {restaurant.name}
                  </h1>
                  <Badge
                    variant={restaurantOpen ? 'success' : 'danger'}
                    className="rounded-md text-xs font-semibold"
                  >
                    {restaurantOpen ? 'Açık' : 'Kapalı'}
                  </Badge>
                </div>
                {!restaurantOpen && restaurant.nextOpenAt && (
                  <p className="text-sm text-gray-500">
                    {formatNextOpen(restaurant.nextOpenAt)} açılıyor
                  </p>
                )}
                {restaurantOpen && restaurant.nextCloseAt && (
                  <p className="text-sm text-gray-500">
                    Bugün {formatNextClose(restaurant.nextCloseAt)} kapanıyor
                  </p>
                )}
              </div>
              <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 sm:ml-auto sm:flex-shrink-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Icon name="star" size={16} className="text-amber-500 fill-amber-500 shrink-0" aria-hidden />
                  <span className="font-semibold text-gray-800">{formatRating(restaurant.rating)}</span>
                  {Number(restaurant.reviewCount) > 0 && (
                    <span className="text-gray-500 font-normal">
                      ({Number(restaurant.reviewCount)} değerlendirme)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Icon name="clock" size={16} className="text-gray-400" aria-hidden />
                  <span>{restaurant.estimatedPreparationMinutes} dk</span>
                </div>
                <div>
                  Teslimat {formatCurrency(restaurant.deliveryFee ?? restaurant.serviceZone?.deliveryFee ?? 0)}
                </div>
                <div>
                  Min. {formatCurrency(restaurant.minimumOrderAmount ?? restaurant.serviceZone?.minimumOrderAmount ?? 0)}
                </div>
              </dl>
            </div>
          </div>
        </article>

        {restaurant.description && (
          <p className="mt-5 text-gray-600 text-sm sm:text-base leading-relaxed max-w-3xl">
            {restaurant.description}
          </p>
        )}
      </div>

      {promotions.length > 0 && (
        <div className="mb-6 space-y-3">
          {promotions.map((promotion) => {
            const getPromotionLabel = () => {
              if (promotion.type === 'FREE_DELIVERY') return 'Ücretsiz Teslimat';
              if (promotion.type === 'DISCOUNT_PERCENTAGE') return `%${promotion.discountValue} İndirim`;
              if (promotion.type === 'DISCOUNT_AMOUNT') return `${promotion.discountValue || 0}₺ İndirim`;
              return promotion.name;
            };

            const getPromotionDescription = () => {
              if (promotion.minimumOrderAmount) {
                return `${promotion.minimumOrderAmount}₺ ve üzeri siparişlerde geçerli`;
              }
              return promotion.description || '';
            };

            return (
              <div
                key={promotion.id}
                className="bg-gradient-to-r from-[#E63946] to-[#FF6B6B] rounded-2xl p-4 text-white shadow-lg"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">🎉</span>
                      <h3 className="font-bold text-lg">{promotion.name}</h3>
                    </div>
                    <p className="text-white/90 text-sm mb-2">{getPromotionLabel()}</p>
                    {getPromotionDescription() && (
                      <p className="text-white/80 text-xs">{getPromotionDescription()}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                      Kampanya
                    </Badge>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mb-6">
        <div className="relative">
          <Icon
            name="search"
            size={20}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Menüde ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
      </div>

      {/*
        Horizontal sticky category rail — visible up to xl breakpoint.
        Pattern used by every major food-delivery platform:
          - Native horizontal scroll (every category remains accessible)
          - Sticky beneath the header so navigation works at any scroll depth
          - Edge gradient mask hints at off-screen content
          - scrollspy-driven active state (see IntersectionObserver effect)
          - Auto-centers active tab as the user scrolls vertically
      */}
      {menu.length > 0 && (
        <div className="xl:hidden sticky top-20 z-30 -mx-4 mb-6 bg-background/90 backdrop-blur-md border-b border-gray-100">
          <div
            ref={horizontalTabBarRef}
            role="tablist"
            aria-label="Menü kategorileri"
            className="flex flex-nowrap gap-2.5 overflow-x-auto scrollbar-hide px-4 py-2.5 [mask-image:linear-gradient(to_right,transparent_0,black_20px,black_calc(100%-20px),transparent_100%)]"
            style={{ scrollSnapType: 'x proximity' }}
          >
            {menu.map((category) => {
              const isActive = activeCategoryId === category.id;
              const itemCount = itemCountFor(category);
              return (
                <button
                  key={category.id}
                  ref={setTabRef(category.id)}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  title={category.name}
                  onClick={() => scrollToCategory(category.id)}
                  style={{ scrollSnapAlign: 'center' }}
                  className={cn(
                    'shrink-0 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap',
                    'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300',
                  )}
                >
                  <span>{category.name}</span>
                  {itemCount > 0 && (
                    <span className={cn('text-xs tabular-nums', isActive ? 'text-white/75' : 'text-gray-400')}>
                      {itemCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/*
        Two-column layout on ≥1280px (xl):
          - Left: vertical sticky category rail (Wolt/DoorDash-style desktop pattern)
          - Right: menu sections grid
        Below xl, this collapses to a single column and navigation falls back
        to the horizontal sticky bar above.
      */}
      <div className="xl:grid xl:grid-cols-[260px_1fr] xl:gap-10">
        {menu.length > 0 && (
          <aside className="hidden xl:block">
            <nav
              aria-label="Menü kategorileri"
              className="sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto scrollbar-hide pr-2"
            >
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3 px-3">
                Kategoriler
              </h2>
              <ul className="space-y-1">
                {menu.map((category) => {
                  const isActive = activeCategoryId === category.id;
                  const itemCount = itemCountFor(category);
                  return (
                    <li key={category.id}>
                      <button
                        type="button"
                        aria-current={isActive ? 'true' : undefined}
                        onClick={() => scrollToCategory(category.id)}
                        className={cn(
                          'group w-full flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm text-left',
                          'transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                          isActive
                            ? 'bg-primary text-white font-semibold shadow-sm'
                            : 'text-gray-700 hover:bg-gray-100',
                        )}
                      >
                        <span className="truncate">{category.name}</span>
                        {itemCount > 0 && (
                          <span
                            className={cn(
                              'shrink-0 text-xs tabular-nums rounded-full px-2 py-0.5',
                              isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-white',
                            )}
                          >
                            {itemCount}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>
        )}

        <div className="min-w-0">
          {filteredMenu.length > 0 ? (
            filteredMenu.map((category) => {
              const items = category.menuItems || (category as any).products || [];
              if (items.length === 0) return null;

              return (
                <section
                  key={category.id}
                  id={`kategori-${category.id}`}
                  ref={setSectionRef(category.id)}
                  data-category-id={category.id}
                  className={cn('mb-12', SCROLL_MARGIN_CLASS)}
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{category.name}</h2>
                  {category.description && (
                    <p className="text-gray-600 mb-4">{category.description}</p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((item: MenuItem) => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        onAddToCart={() => handleProductClick(item)}
                        addToCartDisabled={!restaurantOpen}
                      />
                    ))}
                  </div>
                </section>
              );
            })
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>
                {searchQuery
                  ? `"${searchQuery}" için sonuç bulunamadı.`
                  : 'Bu restoran için menü bulunamadı.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {selectedProduct && (
        <ProductCustomizationModal
          product={selectedProduct}
          isOpen={!!selectedProduct}
          onClose={handleCloseModal}
          onAddToCart={handleAddToCart}
          addToCartDisabled={!restaurantOpen}
        />
      )}
    </MainLayout>
  );
};

export default RestaurantPage;
